import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';

// One code on one dimension (Figma "Code chip"), a pill. Pickers pass onClick and `selected`; a value in a
// table or comparison is `coded`; an uncoded cell has no code and shows a dash.
const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    height: 28,
    px: 1.25,
    borderRadius: 999,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
};

const CodeChip = ({ code, selected = false, coded = false, onClick, disabled = false, title, ...rest }) => {
    if (!code) {
        return (
            <Box component="span" aria-label="Not coded" sx={{ ...base, color: 'text.disabled', fontWeight: 500 }} {...rest}>
                —
            </Box>
        );
    }

    if (!onClick) {
        return (
            <Box
                component="span"
                title={title}
                sx={{
                    ...base,
                    color: 'text.primary',
                    bgcolor: coded ? 'action.hover' : 'background.paper',
                    border: coded ? 'none' : '1px solid',
                    borderColor: 'divider',
                }}
                {...rest}
            >
                {code}
            </Box>
        );
    }

    return (
        <ButtonBase
            role="radio"
            aria-checked={selected}
            title={title}
            disabled={disabled}
            onClick={onClick}
            sx={{
                ...base,
                height: 32,
                minWidth: 44,
                px: 1.5,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'primary.main' : 'background.paper',
                color: selected ? 'primary.contrastText' : 'text.primary',
                transition: (theme) => theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow', 'transform'], {
                    duration: theme.transitions.duration.shortest,
                }),
                '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => (selected ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.06)),
                },
                '&:active': { transform: 'scale(0.96)' },
                '&.Mui-focusVisible': {
                    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                '&.Mui-disabled': { opacity: selected ? 0.7 : 0.45 },
            }}
            {...rest}
        >
            {code}
        </ButtonBase>
    );
};

export default CodeChip;
